import React from "react";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, ShieldCheck, LogOut } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { NotificationBell } from "@/components/platform/NotificationBell";
import { TenantSwitcher } from "@/components/platform/TenantSwitcher";
import { SupportModeBar } from "@/components/platform/SupportMode";

const NAV = [
  { to: "/dashboard", label: "Assessments", module: "dashboard" },
  { to: "/tasks", label: "Tasks", module: "tasks" },
  { to: "/approvals", label: "Approvals", module: "approvals" },
  { to: "/audit", label: "Audit Log", module: "audit_log" },
  { to: "/files", label: "Files", module: "files" },
  { to: "/settings", label: "Settings", module: "tenant_settings" },
];

const OPERATIONS = [
  { to: "/rights", label: "Rights Requests", module: "requests", hint: "Access, correction, deletion, nominee, grievance" },
  { to: "/vendors", label: "Vendor Register", module: "vendors", hint: "Inventory, RoPA links, risk, assessments, contracts" },
  { to: "/retention", label: "Retention Manager", module: "retention", hint: "Rules, disposition queue, holds, certificates" },
  { to: "/controls", label: "Control Library", module: "controls", hint: "Controls, procedures, executions, testing, deficiencies" },
  { to: "/breach", label: "Breach Desk", module: "incidents", hint: "Incidents, 72-hour timer, decisions, reports, evidence packs" },
];

const OperationsMenu = () => {
  const auth = useAuth();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const items = OPERATIONS.filter((o) => auth.can(o.module, "view"));
  if (!items.length) return null;
  const active = items.some((o) => o.to && pathname.startsWith(o.to));
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid="nav-operations"
        className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-[13px] font-medium transition-colors outline-none shrink-0 whitespace-nowrap ${
          active ? "bg-[#003366] text-white" : "text-slate-600 hover:bg-slate-100 hover:text-[#003366]"}`}
      >
        Operations <ChevronDown className="w-3.5 h-3.5" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64" data-testid="nav-operations-menu">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-400">Operational compliance</DropdownMenuLabel>
        {items.map((o) => (
          <DropdownMenuItem key={o.label} disabled={!o.to} onSelect={() => o.to && nav(o.to)}
                            data-testid={`nav-ops-${o.label.toLowerCase().replace(/\s+/g, "-")}`} className="flex-col items-start gap-0">
            <span className="text-[13px] font-medium text-[#003366]">{o.label}</span>
            <span className="text-[11px] text-slate-500">{o.hint}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const PlatformNav = () => {
  const auth = useAuth();
  return (
    <nav className="hidden md:flex items-center gap-1 min-w-0 overflow-x-auto flex-nowrap [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="platform-nav">
      {NAV.slice(0, 1).map((n) => (
        <NavLink key={n.to} to={n.to} data-testid="nav-assessments"
                 className={({ isActive }) => `px-3 py-1.5 rounded-lg text-[13px] font-medium transition-colors shrink-0 whitespace-nowrap ${
                   isActive ? "bg-[#003366] text-white" : "text-slate-600 hover:bg-slate-100 hover:text-[#003366]"}`}>
          {n.label}
        </NavLink>
      ))}
      <OperationsMenu />
      {NAV.slice(1).filter((n) => auth.can(n.module, "view")).map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
          className={({ isActive }) =>
            `px-3 py-1.5 rounded-lg text-[13px] font-medium transition-colors shrink-0 whitespace-nowrap ${
              isActive ? "bg-[#003366] text-white" : "text-slate-600 hover:bg-slate-100 hover:text-[#003366]"
            }`
          }
        >
          {n.label}
        </NavLink>
      ))}
    </nav>
  );
};

export const UserChip = () => {
  const auth = useAuth();
  const nav = useNavigate();
  return (
    <>
      <div className="hidden xl:block text-right leading-tight" data-testid="header-username">
        <div className="text-[13px] font-medium text-[#003366] truncate max-w-[180px]">{auth.user?.username}</div>
        <div className="text-[10px] uppercase tracking-wide font-mono text-[#008080]" data-testid="header-role-label">
          {auth.user?.role_label || auth.user?.role}
        </div>
      </div>
      <Button
        variant="ghost" size="sm" className="grc-btn"
        onClick={() => { auth.logout(); nav("/login"); }}
        data-testid="header-logout-button"
      >
        <LogOut className="w-4 h-4 xl:mr-2" /> <span className="hidden xl:inline">Logout</span>
      </Button>
    </>
  );
};

export default function AppShell({ children, testId }) {
  const auth = useAuth();
  return (
    <div className="App min-h-screen bg-[#F7F8FA]">
      {auth.user?.is_demo_tenant && (
        <div className="bg-amber-400 text-amber-950 text-center text-[12px] font-semibold py-1.5 px-4" data-testid="demo-workspace-banner">
          Demo workspace — sample data for demos and POCs, not a live customer organization
        </div>
      )}
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 h-16 flex items-center gap-4">
          <Link to="/dashboard" className="flex items-center gap-2 shrink-0" data-testid="header-logo-link">
            <div className="w-9 h-9 rounded-[8px] bg-[#003366] flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div className="hidden sm:block text-[15px] font-bold tracking-tight text-[#003366]">CX0 GRC</div>
          </Link>
          <PlatformNav />
          <div className="flex-1" />
          <TenantSwitcher />
          <NotificationBell />
          <UserChip />
        </div>
      </header>
      <SupportModeBar />
      <main className="max-w-[1600px] mx-auto px-4 md:px-8 py-8 space-y-6" data-testid={testId}>
        {children}
      </main>
    </div>
  );
}
